import { Check, Save, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { applyLook, deletePreset, overwritePreset, savePreset } from '../../store/presets';
import { LAYOUTS, THEMES, useSettings } from '../../store/settings';
import { buttonClass, ghostButtonClass, inputClass, Section } from './controls';

/** Save the current look under a name, and switch between saved looks. */
export function PresetsSection() {
  const presets = useSettings((s) => s.presets);
  const [name, setName] = useState('');
  const [applied, setApplied] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const save = (e: FormEvent) => {
    e.preventDefault();
    const p = savePreset(name || `Layout ${presets.length + 1}`);
    setName('');
    setApplied(p.id);
  };

  return (
    <Section
      title="Saved layouts"
      description="Save how this screen looks – layout, theme, size, which widgets show and how – and switch back any time. Photos, calendars, feeds and location aren’t included, so a saved layout works on any screen."
    >
      <form onSubmit={save} className="flex gap-2">
        <input
          className={inputClass}
          placeholder="Name, e.g. Kitchen tablet or Evening"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          aria-label="Layout name"
        />
        <button className={buttonClass} type="submit">
          <Save className="h-4 w-4" aria-hidden /> Save
        </button>
      </form>
      {presets.length > 0 && (
        <ul className="space-y-2">
          {presets.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--border)] px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{p.name}</div>
                <div className="muted truncate text-xs">
                  {LAYOUTS.find((l) => l.id === p.look.layout)?.name} · {THEMES.find((t) => t.id === p.look.theme)?.name}
                </div>
              </div>
              <button
                className={buttonClass}
                onClick={() => {
                  applyLook(p.look);
                  setApplied(p.id);
                }}
              >
                {applied === p.id ? <Check className="h-4 w-4" aria-hidden /> : null} Apply
              </button>
              <button className={ghostButtonClass} onClick={() => overwritePreset(p.id)} title="Replace with the current look">
                Update
              </button>
              {confirmDelete === p.id ? (
                <button
                  className={`${ghostButtonClass} border-red-400 text-red-400`}
                  onClick={() => {
                    deletePreset(p.id);
                    setConfirmDelete(null);
                  }}
                >
                  Delete?
                </button>
              ) : (
                <button className={ghostButtonClass} onClick={() => setConfirmDelete(p.id)} aria-label={`Delete ${p.name}`}>
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="muted text-xs">Saved layouts sync to every screen signed in to your account.</p>
    </Section>
  );
}
